/**
 * Conference & Show Coordinator — per-show workspace (spec §8).
 *
 * The detail surface behind the roll-up: opens one Event document and lets the
 * coordinator edit every section — Overview, Team (with travel / hotel /
 * registration per member), Tasks, Booth Coverage, Itinerary, Expenses, Notes.
 * Edits are held locally and saved as the whole Event doc via
 * PUT /api/events/{id} (single source of truth for the roll-up + calendar).
 *
 * Scaffold scope: full read + edit + save across the sections. Not yet here:
 * the New Event scaffolder (create), library management, and per-field
 * validation beyond the basics.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  makeStyles,
  tokens,
  Text,
  Spinner,
  Button,
  Input,
  Textarea,
  Field,
  Dropdown,
  Option,
  TabList,
  Tab,
  Card,
  Badge,
  Checkbox,
} from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Save20Regular,
  Add20Regular,
  Delete20Regular,
  ArrowClockwise20Regular,
} from "@fluentui/react-icons";

import useCrmApiClient from "../../hooks/useCrmApiClient";

// ---------------------------------------------------------------------------
// Types (mirror events_models.py Event document)
// ---------------------------------------------------------------------------

interface Milestones {
  show_start?: string | null; show_end?: string | null;
  expo_open?: string | null; expo_close?: string | null;
  move_in?: string | null; move_out?: string | null;
}
interface Venue { name: string; address: string; city: string; state: string; }
interface Booth { number: string; size: string; }
interface KeyContact { role: string; name: string; phone: string; email: string; }
interface Resource { label: string; url: string; }
interface TravelInfo {
  airline: string; flight_no: string; depart_airport: string; depart_date: string;
  depart_time: string; arrive_airport: string; arrive_date: string; arrive_time: string;
  return_flight_no: string; return_date: string; return_time: string; ground: string;
  confirmation_no: string;
}
interface HotelInfo {
  name: string; confirmation_no: string; check_in: string; check_out: string;
  nights?: number | null; roommate: string; payment: string; shuttle: string;
}
interface RegistrationInfo {
  type: string; confirmation_no: string; badge_status: string; chiefs_night: boolean;
  banquet: boolean; banquet_cost?: number | null; other_event: string;
  cost?: number | null; paid: boolean;
}
interface TeamMember {
  id: string; name: string; role: string; cell: string; email: string;
  emergency_contact: string; badge_type: string; notes: string;
  travel: TravelInfo; hotel: HotelInfo; registration: RegistrationInfo;
}
interface TaskInstance {
  id: string; template_key?: string | null; name: string; category: string;
  scope: string; assignee_team_member_id?: string | null; anchor: string;
  lead_days?: number | null; due_date?: string | null; nudge_lead_days: number;
  snooze_until?: string | null; owner: string; status: string; notes: string;
}
interface BoothCoverageSlot { date: string; time_block: string; assignments: string[]; notes: string; }
interface ItineraryItem {
  date: string; time: string; task: string; location: string;
  required_team: string; owner: string; notes: string;
}
interface Expense {
  date: string; employee: string; category: string; vendor: string; description: string;
  amount?: number | null; paid_by: string; receipt: boolean; reimbursable: boolean;
  gl: string; notes: string;
}
interface Note { ts: string; author: string; body: string; }

export interface EventDoc {
  id: string; coordinator: string; name: string; org: string; status: string;
  venue: Venue; booth: Booth; hotel_primary: string; milestones: Milestones;
  key_contacts: KeyContact[]; resources: Resource[]; team: TeamMember[];
  tasks: TaskInstance[]; booth_coverage: BoothCoverageSlot[];
  itinerary: ItineraryItem[]; expenses: Expense[]; notes: Note[];
  created_at: string; updated_at: string;
}

const EVENT_STATUSES = ["Considering", "Committed", "In Prep", "Onsite", "Follow-up", "Closed"];
const TASK_STATUSES = ["Not Started", "In Progress", "Done"];

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: { height: "100%", display: "flex", flexDirection: "column" },
  center: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", height: "100%", gap: tokens.spacingVerticalM, textAlign: "center",
  },
  topbar: {
    display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  title: { flex: 1, minWidth: 0 },
  body: { flex: 1, overflowY: "auto", padding: tokens.spacingVerticalL, maxWidth: "980px", width: "100%", margin: "0 auto", boxSizing: "border-box" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalM },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalM },
  sectionTitle: {
    fontSize: tokens.fontSizeBase200, textTransform: "uppercase", letterSpacing: "0.04em",
    color: tokens.colorNeutralForeground3, margin: `${tokens.spacingVerticalL} 0 ${tokens.spacingVerticalS}`,
  },
  memberCard: { padding: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalM },
  memberHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: tokens.spacingVerticalS },
  row: { display: "flex", gap: tokens.spacingHorizontalS, alignItems: "flex-end", marginBottom: tokens.spacingVerticalS, flexWrap: "wrap" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: tokens.fontSizeBase300 },
  th: { textAlign: "left", padding: tokens.spacingVerticalXS, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  td: { padding: "2px", borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, verticalAlign: "top" },
  savebar: {
    display: "flex", alignItems: "center", gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM, borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  note: { padding: tokens.spacingVerticalS, borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  grow: { flex: 1, minWidth: "120px" },
});

// ---------------------------------------------------------------------------
// Small controlled-field helpers
// ---------------------------------------------------------------------------

const TextField: React.FC<{
  label?: string; value: string | null | undefined; onChange: (v: string) => void;
  type?: "text" | "date" | "number"; placeholder?: string; className?: string;
}> = ({ label, value, onChange, type, placeholder, className }) => (
  <Field label={label} className={className}>
    <Input value={value ?? ""} type={type} placeholder={placeholder}
      onChange={(_, d) => onChange(d.value)} />
  </Field>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  eventId: string;
  coordinator?: string;
  onBack: () => void;
}

export const ConferencesWorkspace: React.FC<Props> = ({ eventId, coordinator, onBack }) => {
  const styles = useStyles();
  const crmApi = useCrmApiClient();
  const crmApiRef = React.useRef(crmApi);
  crmApiRef.current = crmApi;

  const [ev, setEv] = useState<EventDoc | null>(null);
  const [orig, setOrig] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [tab, setTab] = useState<string>("overview");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = coordinator ? `?coordinator=${encodeURIComponent(coordinator)}` : "";
      const doc = await crmApiRef.current.get<EventDoc>(`/api/events/${encodeURIComponent(eventId)}${q}`);
      setEv(doc);
      setOrig(JSON.stringify(doc));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [eventId, coordinator]);

  useEffect(() => { void load(); }, [load]);

  const dirty = useMemo(() => ev !== null && JSON.stringify(ev) !== orig, [ev, orig]);

  // Immutable update helper.
  const update = useCallback((mut: (d: EventDoc) => void) => {
    setEv((prev) => {
      if (!prev) return prev;
      const copy = structuredClone(prev) as EventDoc;
      mut(copy);
      return copy;
    });
    setSaveMsg(null);
  }, []);

  const save = useCallback(async () => {
    if (!ev) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const q = coordinator ? `?coordinator=${encodeURIComponent(coordinator)}` : "";
      const saved = await crmApiRef.current.put<EventDoc>(
        `/api/events/${encodeURIComponent(ev.id)}${q}`, ev,
      );
      setEv(saved);
      setOrig(JSON.stringify(saved));
      setSaveMsg("Saved.");
    } catch (e) {
      setSaveMsg(`Save failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }, [ev, coordinator]);

  if (loading) {
    return <div className={styles.center}><Spinner size="large" /><Text>Loading show…</Text></div>;
  }
  if (error || !ev) {
    return (
      <div className={styles.center}>
        <Text size={500} weight="semibold">Couldn’t load this show</Text>
        <Text style={{ maxWidth: "560px", fontFamily: tokens.fontFamilyMonospace }}>{error}</Text>
        <div style={{ display: "flex", gap: tokens.spacingHorizontalS }}>
          <Button icon={<ArrowLeft20Regular />} onClick={onBack}>Back</Button>
          <Button icon={<ArrowClockwise20Regular />} onClick={() => void load()}>Retry</Button>
        </div>
      </div>
    );
  }

  const expenseTotal = ev.expenses.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className={styles.root}>
      <div className={styles.topbar}>
        <Button appearance="subtle" icon={<ArrowLeft20Regular />} onClick={onBack}>My Day</Button>
        <div className={styles.title}>
          <Text weight="semibold" size={500} block>{ev.name || "(unnamed show)"}</Text>
        </div>
        <Badge appearance="tint">{ev.status}</Badge>
      </div>

      <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as string)} style={{ padding: `0 ${tokens.spacingHorizontalM}` }}>
        <Tab value="overview">Overview</Tab>
        <Tab value="team">Team ({ev.team.length})</Tab>
        <Tab value="tasks">Tasks ({ev.tasks.length})</Tab>
        <Tab value="booth">Booth</Tab>
        <Tab value="itinerary">Itinerary</Tab>
        <Tab value="expenses">Expenses</Tab>
        <Tab value="notes">Notes ({ev.notes.length})</Tab>
      </TabList>

      <div className={styles.body}>
        {tab === "overview" && (
          <>
            <div className={styles.grid2}>
              <TextField label="Show name" value={ev.name} onChange={(v) => update((d) => { d.name = v; })} />
              <TextField label="Organization" value={ev.org} onChange={(v) => update((d) => { d.org = v; })} />
            </div>
            <div className={styles.grid3}>
              <Field label="Status">
                <Dropdown value={ev.status} selectedOptions={[ev.status]}
                  onOptionSelect={(_, d) => update((e) => { e.status = (d.optionValue as string) || e.status; })}>
                  {EVENT_STATUSES.map((s) => <Option key={s} value={s}>{s}</Option>)}
                </Dropdown>
              </Field>
              <TextField label="Booth #" value={ev.booth.number} onChange={(v) => update((d) => { d.booth.number = v; })} />
              <TextField label="Booth size" value={ev.booth.size} onChange={(v) => update((d) => { d.booth.size = v; })} />
            </div>
            <div className={styles.sectionTitle}>Venue</div>
            <div className={styles.grid2}>
              <TextField label="Venue name" value={ev.venue.name} onChange={(v) => update((d) => { d.venue.name = v; })} />
              <TextField label="Address" value={ev.venue.address} onChange={(v) => update((d) => { d.venue.address = v; })} />
              <TextField label="City" value={ev.venue.city} onChange={(v) => update((d) => { d.venue.city = v; })} />
              <TextField label="State" value={ev.venue.state} onChange={(v) => update((d) => { d.venue.state = v; })} />
            </div>
            <TextField label="Primary hotel" value={ev.hotel_primary} onChange={(v) => update((d) => { d.hotel_primary = v; })} />

            <div className={styles.sectionTitle}>Milestones</div>
            <div className={styles.grid3}>
              <TextField label="Show start" type="date" value={ev.milestones.show_start ?? ""} onChange={(v) => update((d) => { d.milestones.show_start = v || null; })} />
              <TextField label="Show end" type="date" value={ev.milestones.show_end ?? ""} onChange={(v) => update((d) => { d.milestones.show_end = v || null; })} />
              <TextField label="Expo open" type="date" value={ev.milestones.expo_open ?? ""} onChange={(v) => update((d) => { d.milestones.expo_open = v || null; })} />
              <TextField label="Expo close" type="date" value={ev.milestones.expo_close ?? ""} onChange={(v) => update((d) => { d.milestones.expo_close = v || null; })} />
              <TextField label="Move-in" type="date" value={ev.milestones.move_in ?? ""} onChange={(v) => update((d) => { d.milestones.move_in = v || null; })} />
              <TextField label="Move-out" type="date" value={ev.milestones.move_out ?? ""} onChange={(v) => update((d) => { d.milestones.move_out = v || null; })} />
            </div>

            <div className={styles.sectionTitle}>Key contacts</div>
            {ev.key_contacts.map((c, i) => (
              <div className={styles.row} key={i}>
                <TextField className={styles.grow} label={i === 0 ? "Role" : undefined} value={c.role} onChange={(v) => update((d) => { d.key_contacts[i].role = v; })} />
                <TextField className={styles.grow} label={i === 0 ? "Name" : undefined} value={c.name} onChange={(v) => update((d) => { d.key_contacts[i].name = v; })} />
                <TextField className={styles.grow} label={i === 0 ? "Phone" : undefined} value={c.phone} onChange={(v) => update((d) => { d.key_contacts[i].phone = v; })} />
                <TextField className={styles.grow} label={i === 0 ? "Email" : undefined} value={c.email} onChange={(v) => update((d) => { d.key_contacts[i].email = v; })} />
                <Button icon={<Delete20Regular />} appearance="subtle" onClick={() => update((d) => { d.key_contacts.splice(i, 1); })} />
              </div>
            ))}
            <Button size="small" icon={<Add20Regular />} onClick={() => update((d) => { d.key_contacts.push({ role: "", name: "", phone: "", email: "" }); })}>Add contact</Button>

            <div className={styles.sectionTitle}>Resources</div>
            {ev.resources.map((r, i) => (
              <div className={styles.row} key={i}>
                <TextField className={styles.grow} label={i === 0 ? "Label" : undefined} value={r.label} onChange={(v) => update((d) => { d.resources[i].label = v; })} />
                <TextField className={styles.grow} label={i === 0 ? "URL" : undefined} value={r.url} onChange={(v) => update((d) => { d.resources[i].url = v; })} />
                <Button icon={<Delete20Regular />} appearance="subtle" onClick={() => update((d) => { d.resources.splice(i, 1); })} />
              </div>
            ))}
            <Button size="small" icon={<Add20Regular />} onClick={() => update((d) => { d.resources.push({ label: "", url: "" }); })}>Add resource</Button>
          </>
        )}

        {tab === "team" && (
          <>
            {ev.team.map((m, i) => (
              <Card key={m.id} className={styles.memberCard}>
                <div className={styles.memberHead}>
                  <Text weight="semibold">{m.name || "(new member)"}</Text>
                  <Button icon={<Delete20Regular />} appearance="subtle" size="small"
                    onClick={() => update((d) => { d.team.splice(i, 1); })}>Remove</Button>
                </div>
                <div className={styles.grid3}>
                  <TextField label="Name" value={m.name} onChange={(v) => update((d) => { d.team[i].name = v; })} />
                  <TextField label="Role" value={m.role} onChange={(v) => update((d) => { d.team[i].role = v; })} />
                  <TextField label="Email" value={m.email} onChange={(v) => update((d) => { d.team[i].email = v; })} />
                  <TextField label="Cell" value={m.cell} onChange={(v) => update((d) => { d.team[i].cell = v; })} />
                  <TextField label="Badge type" value={m.badge_type} onChange={(v) => update((d) => { d.team[i].badge_type = v; })} />
                  <TextField label="Emergency contact" value={m.emergency_contact} onChange={(v) => update((d) => { d.team[i].emergency_contact = v; })} />
                </div>
                <div className={styles.sectionTitle}>Travel</div>
                <div className={styles.grid3}>
                  <TextField label="Airline" value={m.travel.airline} onChange={(v) => update((d) => { d.team[i].travel.airline = v; })} />
                  <TextField label="Flight #" value={m.travel.flight_no} onChange={(v) => update((d) => { d.team[i].travel.flight_no = v; })} />
                  <TextField label="Confirmation #" value={m.travel.confirmation_no} onChange={(v) => update((d) => { d.team[i].travel.confirmation_no = v; })} />
                </div>
                <div className={styles.sectionTitle}>Hotel</div>
                <div className={styles.grid3}>
                  <TextField label="Hotel" value={m.hotel.name} onChange={(v) => update((d) => { d.team[i].hotel.name = v; })} />
                  <TextField label="Confirmation #" value={m.hotel.confirmation_no} onChange={(v) => update((d) => { d.team[i].hotel.confirmation_no = v; })} />
                  <TextField label="Check-in" type="date" value={m.hotel.check_in} onChange={(v) => update((d) => { d.team[i].hotel.check_in = v; })} />
                  <TextField label="Check-out" type="date" value={m.hotel.check_out} onChange={(v) => update((d) => { d.team[i].hotel.check_out = v; })} />
                </div>
                <div className={styles.sectionTitle}>Registration</div>
                <div className={styles.grid3}>
                  <TextField label="Type" value={m.registration.type} onChange={(v) => update((d) => { d.team[i].registration.type = v; })} />
                  <TextField label="Confirmation #" value={m.registration.confirmation_no} onChange={(v) => update((d) => { d.team[i].registration.confirmation_no = v; })} />
                  <TextField label="Badge status" value={m.registration.badge_status} onChange={(v) => update((d) => { d.team[i].registration.badge_status = v; })} />
                </div>
                <div className={styles.row} style={{ marginTop: tokens.spacingVerticalS }}>
                  <Checkbox label="Paid" checked={m.registration.paid} onChange={(_, d) => update((e) => { e.team[i].registration.paid = !!d.checked; })} />
                  <Checkbox label="Chiefs Night" checked={m.registration.chiefs_night} onChange={(_, d) => update((e) => { e.team[i].registration.chiefs_night = !!d.checked; })} />
                  <Checkbox label="Banquet" checked={m.registration.banquet} onChange={(_, d) => update((e) => { e.team[i].registration.banquet = !!d.checked; })} />
                </div>
              </Card>
            ))}
            <Button icon={<Add20Regular />} onClick={() => update((d) => {
              const id = `m-${Date.now()}-${d.team.length}`;
              d.team.push({
                id, name: "", role: "", cell: "", email: "", emergency_contact: "",
                badge_type: "", notes: "",
                travel: { airline: "", flight_no: "", depart_airport: "", depart_date: "", depart_time: "", arrive_airport: "", arrive_date: "", arrive_time: "", return_flight_no: "", return_date: "", return_time: "", ground: "", confirmation_no: "" },
                hotel: { name: "", confirmation_no: "", check_in: "", check_out: "", nights: null, roommate: "", payment: "", shuttle: "" },
                registration: { type: "", confirmation_no: "", badge_status: "", chiefs_night: false, banquet: false, banquet_cost: null, other_event: "", cost: null, paid: false },
              });
            })}>Add team member</Button>
          </>
        )}

        {tab === "tasks" && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Task</th><th className={styles.th}>Scope / who</th>
                <th className={styles.th}>Due</th><th className={styles.th}>Status</th>
                <th className={styles.th}>Owner</th><th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {ev.tasks.map((t, i) => {
                const who = t.scope === "per_person"
                  ? (ev.team.find((m) => m.id === t.assignee_team_member_id)?.name || "—")
                  : "event";
                return (
                  <tr key={t.id}>
                    <td className={styles.td}><Input value={t.name} onChange={(_, d) => update((e) => { e.tasks[i].name = d.value; })} /></td>
                    <td className={styles.td}><Badge appearance="outline" color={t.scope === "per_person" ? "brand" : "subtle"}>{who}</Badge></td>
                    <td className={styles.td}><Input type="date" value={t.due_date ?? ""} onChange={(_, d) => update((e) => { e.tasks[i].due_date = d.value || null; })} /></td>
                    <td className={styles.td}>
                      <Dropdown value={t.status} selectedOptions={[t.status]} onOptionSelect={(_, d) => update((e) => { e.tasks[i].status = (d.optionValue as string) || e.tasks[i].status; })}>
                        {TASK_STATUSES.map((s) => <Option key={s} value={s}>{s}</Option>)}
                      </Dropdown>
                    </td>
                    <td className={styles.td}><Input value={t.owner} onChange={(_, d) => update((e) => { e.tasks[i].owner = d.value; })} /></td>
                    <td className={styles.td}><Button icon={<Delete20Regular />} appearance="subtle" onClick={() => update((e) => { e.tasks.splice(i, 1); })} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {tab === "booth" && (
          <table className={styles.table}>
            <thead><tr><th className={styles.th}>Date</th><th className={styles.th}>Time block</th><th className={styles.th}>Notes</th><th className={styles.th}></th></tr></thead>
            <tbody>
              {ev.booth_coverage.map((b, i) => (
                <tr key={i}>
                  <td className={styles.td}><Input value={b.date} onChange={(_, d) => update((e) => { e.booth_coverage[i].date = d.value; })} /></td>
                  <td className={styles.td}><Input value={b.time_block} onChange={(_, d) => update((e) => { e.booth_coverage[i].time_block = d.value; })} /></td>
                  <td className={styles.td}><Input value={b.notes} onChange={(_, d) => update((e) => { e.booth_coverage[i].notes = d.value; })} /></td>
                  <td className={styles.td}><Button icon={<Delete20Regular />} appearance="subtle" onClick={() => update((e) => { e.booth_coverage.splice(i, 1); })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === "itinerary" && (
          <table className={styles.table}>
            <thead><tr><th className={styles.th}>Date</th><th className={styles.th}>Time</th><th className={styles.th}>Task</th><th className={styles.th}>Location</th><th className={styles.th}>Owner</th><th className={styles.th}></th></tr></thead>
            <tbody>
              {ev.itinerary.map((it, i) => (
                <tr key={i}>
                  <td className={styles.td}><Input value={it.date} onChange={(_, d) => update((e) => { e.itinerary[i].date = d.value; })} /></td>
                  <td className={styles.td}><Input value={it.time} onChange={(_, d) => update((e) => { e.itinerary[i].time = d.value; })} /></td>
                  <td className={styles.td}><Input value={it.task} onChange={(_, d) => update((e) => { e.itinerary[i].task = d.value; })} /></td>
                  <td className={styles.td}><Input value={it.location} onChange={(_, d) => update((e) => { e.itinerary[i].location = d.value; })} /></td>
                  <td className={styles.td}><Input value={it.owner} onChange={(_, d) => update((e) => { e.itinerary[i].owner = d.value; })} /></td>
                  <td className={styles.td}><Button icon={<Delete20Regular />} appearance="subtle" onClick={() => update((e) => { e.itinerary.splice(i, 1); })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === "expenses" && (
          <>
            <Text weight="semibold" block style={{ marginBottom: tokens.spacingVerticalS }}>
              Total: ${expenseTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
            <table className={styles.table}>
              <thead><tr><th className={styles.th}>Category</th><th className={styles.th}>Vendor</th><th className={styles.th}>Amount</th><th className={styles.th}>Paid by</th><th className={styles.th}></th></tr></thead>
              <tbody>
                {ev.expenses.map((x, i) => (
                  <tr key={i}>
                    <td className={styles.td}><Input value={x.category} onChange={(_, d) => update((e) => { e.expenses[i].category = d.value; })} /></td>
                    <td className={styles.td}><Input value={x.vendor} onChange={(_, d) => update((e) => { e.expenses[i].vendor = d.value; })} /></td>
                    <td className={styles.td}><Input type="number" value={x.amount != null ? String(x.amount) : ""} onChange={(_, d) => update((e) => { e.expenses[i].amount = d.value === "" ? null : Number(d.value); })} /></td>
                    <td className={styles.td}><Input value={x.paid_by} onChange={(_, d) => update((e) => { e.expenses[i].paid_by = d.value; })} /></td>
                    <td className={styles.td}><Button icon={<Delete20Regular />} appearance="subtle" onClick={() => update((e) => { e.expenses.splice(i, 1); })} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Button size="small" icon={<Add20Regular />} style={{ marginTop: tokens.spacingVerticalS }}
              onClick={() => update((d) => { d.expenses.push({ date: "", employee: "", category: "", vendor: "", description: "", amount: null, paid_by: "", receipt: false, reimbursable: false, gl: "", notes: "" }); })}>Add expense</Button>
          </>
        )}

        {tab === "notes" && (
          <>
            {ev.notes.length === 0 && <Text style={{ color: tokens.colorNeutralForeground3 }}>No notes yet.</Text>}
            {ev.notes.map((n, i) => (
              <div className={styles.note} key={i}>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{n.author} · {n.ts?.slice(0, 10)}</Text>
                <Text block>{n.body}</Text>
              </div>
            ))}
            <NoteComposer onAdd={(body) => update((d) => {
              d.notes.unshift({ ts: new Date().toISOString(), author: ev.coordinator || "Coordinator", body });
            })} />
          </>
        )}
      </div>

      <div className={styles.savebar}>
        <Button appearance="primary" icon={saving ? <Spinner size="tiny" /> : <Save20Regular />}
          disabled={!dirty || saving} onClick={() => void save()}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
        {dirty && !saving && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Unsaved changes</Text>}
        {saveMsg && <Text size={200} style={{ color: saveMsg.startsWith("Save failed") ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1 }}>{saveMsg}</Text>}
      </div>
    </div>
  );
};

const NoteComposer: React.FC<{ onAdd: (body: string) => void }> = ({ onAdd }) => {
  const styles = useStyles();
  const [text, setText] = useState("");
  return (
    <div style={{ marginTop: tokens.spacingVerticalM }}>
      <Textarea className={styles.grow} style={{ width: "100%" }} value={text} placeholder="Add a note (context, a hold/deferral, etc.)"
        onChange={(_, d) => setText(d.value)} />
      <Button size="small" icon={<Add20Regular />} style={{ marginTop: tokens.spacingVerticalXS }}
        disabled={!text.trim()} onClick={() => { onAdd(text.trim()); setText(""); }}>Add note</Button>
    </div>
  );
};

export default ConferencesWorkspace;
